import { useState } from 'react';
import {
  Megaphone, Radio, BarChart3,
  Eye, CheckCircle2, Send,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import { MasterBroadcastTab } from './tabs/MasterBroadcastTab';
import { MasterAuditoriaTab } from './tabs/MasterAuditoriaTab';

export function ComunicadosMasterPage() {
  const [activeTab, setActiveTab] = useState<'broadcast' | 'auditoria'>('auditoria');

  // Carregar KPIs Globais
  const { data: stats } = useQuery({
    queryKey: ['master-comunicados-stats'],
    queryFn: async () => {
      const res = await api.get('/master/comunicados/stats');
      return res.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  return (
    <div className="admin-page animate-fadeIn" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ═══ HERO STRIP MASTER (Layout Lado a Lado Compacto) ═══ */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #1e293b 100%)',
        borderRadius: 14, padding: '16px 22px', color: '#ffffff',
        position: 'relative', overflow: 'hidden', boxShadow: '0 3px 14px rgba(0,0,0,.15)',
        border: '1px solid rgba(255,255,255,0.08)',
      }}>
        {/* Glow circles decorativos */}
        <div style={{
          position: 'absolute', top: -30, right: -30, width: 140, height: 140,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 70%)',
        }} />
        <div style={{
          position: 'absolute', bottom: -40, left: '25%', width: 180, height: 180,
          borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 70%)',
        }} />

        <div style={{
          position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', flexWrap: 'wrap', gap: 16,
        }}>
          {/* Lado Esquerdo: Título & Subtítulo */}
          <div style={{ flex: '1 1 340px', minWidth: 280 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '3px 10px', borderRadius: 20, background: 'rgba(99,102,241,0.2)',
              color: '#a5b4fc', fontSize: '.72rem', fontWeight: 700, marginBottom: 6,
              border: '1px solid rgba(99,102,241,0.3)',
            }}>
              <Radio size={11} /> COMANDO MASTER · GOVERNANÇA DE COMUNICAÇÃO
            </div>
            <h1 style={{ margin: '0 0 4px', fontSize: '1.25rem', fontWeight: 800, color: '#ffffff', letterSpacing: '-0.01em' }}>
              Central de Comunicados da Plataforma
            </h1>
            <p style={{ margin: 0, fontSize: '.82rem', color: '#cbd5e1', maxWidth: 560, lineHeight: 1.4 }}>
              Broadcast global da plataforma e auditoria consolidada de avisos de toda a rede de ensino.
            </p>
          </div>

          {/* Lado Direito: KPIs Compactos Lado a Lado (Grid 4 colunas ou 2x2) */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
            gap: 8, flex: '1 1 440px', maxWidth: 540,
          }}>
            <div style={{
              background: 'rgba(255,255,255,0.07)', padding: '8px 12px', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(4px)',
            }}>
              <span style={{ fontSize: '.68rem', color: '#94a3b8', display: 'block', fontWeight: 500 }}>Total</span>
              <strong style={{ fontSize: '1.05rem', color: '#ffffff', fontWeight: 800 }}>{stats?.totalComunicados ?? '—'}</strong>
            </div>

            <div style={{
              background: 'rgba(255,255,255,0.07)', padding: '8px 12px', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(4px)',
            }}>
              <span style={{ fontSize: '.68rem', color: '#94a3b8', display: 'block', fontWeight: 500 }}>Avisos Master</span>
              <strong style={{ fontSize: '1.05rem', color: '#818cf8', fontWeight: 800 }}>{stats?.totalBroadcasts ?? '—'}</strong>
            </div>

            <div style={{
              background: 'rgba(255,255,255,0.07)', padding: '8px 12px', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(4px)',
            }}>
              <span style={{ fontSize: '.68rem', color: '#94a3b8', display: 'block', fontWeight: 500 }}>Visualizações</span>
              <strong style={{ fontSize: '1.05rem', color: '#34d399', fontWeight: 800 }}>{stats?.totalVisualizacoes ?? '—'}</strong>
            </div>

            <div style={{
              background: 'rgba(255,255,255,0.07)', padding: '8px 12px', borderRadius: 8,
              border: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(4px)',
            }}>
              <span style={{ fontSize: '.68rem', color: '#94a3b8', display: 'block', fontWeight: 500 }}>Emitidos Hoje</span>
              <strong style={{ fontSize: '1.05rem', color: '#38bdf8', fontWeight: 800 }}>{stats?.criadosHoje ?? '—'}</strong>
            </div>
          </div>
        </div>
      </div>

      {/* ═══ TAB SWITCHER (Auditoria em 1º Lugar) ═══ */}
      <div style={{ display: 'flex', gap: 10, borderBottom: '1px solid var(--border, #e5e7eb)', paddingBottom: 2 }}>
        <button
          type="button"
          onClick={() => setActiveTab('auditoria')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px',
            borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: '.88rem', transition: 'all .2s ease',
            background: activeTab === 'auditoria' ? 'var(--bg-card, #ffffff)' : 'transparent',
            color: activeTab === 'auditoria' ? '#2563eb' : '#6b7280',
            borderBottom: activeTab === 'auditoria' ? '2.5px solid #2563eb' : '2.5px solid transparent',
          }}
        >
          <BarChart3 size={15} /> Auditoria Consolidada da Rede Nacional
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('broadcast')}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px',
            borderRadius: '8px 8px 0 0', border: 'none', cursor: 'pointer',
            fontWeight: 700, fontSize: '.88rem', transition: 'all .2s ease',
            background: activeTab === 'broadcast' ? 'var(--bg-card, #ffffff)' : 'transparent',
            color: activeTab === 'broadcast' ? '#2563eb' : '#6b7280',
            borderBottom: activeTab === 'broadcast' ? '2.5px solid #2563eb' : '2.5px solid transparent',
          }}
        >
          <Megaphone size={15} /> Mural de Avisos Globais (Broadcast)
        </button>
      </div>

      {/* ═══ ABA ATIVA ═══ */}
      {activeTab === 'auditoria' ? <MasterAuditoriaTab /> : <MasterBroadcastTab />}
    </div>
  );
}
